import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, Polygon, FeatureGroup, Popup } from 'react-leaflet';
import { OpenStreetMapTiles } from '../components/OpenStreetMapTiles';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Trash2, Save, Plus, Shield, Database, RefreshCw } from 'lucide-react';
import { I270_POLYGON } from '../lib/i270';
import { POPULATION_ZONES, densityToOpacity, densityToLabel } from '../lib/populationZones';
import { isValidPolygon, safePolygonPositions } from '../lib/leaflet-helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Density → suggested impressions/hour (admin can override before saving).
// Rough calibration: ~ density × 0.55, rounded to nearest 100.
// Caps at 6500/hr for the densest urban core.
const densityToSuggestedRate = (density) => {
  const raw = Math.min(6500, Math.max(500, density * 0.55));
  return Math.round(raw / 100) * 100;
};

const AdminGeofences = () => {
  const [geofences, setGeofences] = useState([]);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneRate, setNewZoneRate] = useState('');
  const [newZoneColor, setNewZoneColor] = useState('#3388ff');
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const featureGroupRef = useRef(null);

  const [showHeatmap, setShowHeatmap] = useState(true);
  const [derivingId, setDerivingId] = useState(null);
  const [deriveAllBusy, setDeriveAllBusy] = useState(false);
  const [recalcBusy, setRecalcBusy] = useState(false);

  const deriveRate = async (gf) => {
    setDerivingId(gf.id);
    setMessage(`Pulling ODOT traffic counts for "${gf.name}"...`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/geofences/${gf.id}/derive-rate`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(`Could not derive "${gf.name}": ${data.detail || res.status}`);
        return;
      }
      const src = data.rate_source;
      setGeofences(prev => prev.map(z =>
        z.id === gf.id ? { ...z, impressions_per_hour: data.new_rate, rate_source: src } : z
      ));
      setMessage(
        `"${gf.name}": ${data.old_rate.toLocaleString()} → ${data.new_rate.toLocaleString()} impr/hr ` +
        `(volume-weighted AADT ${src.aadt_weighted.toLocaleString()} across ${src.segment_count} ODOT segments, ${src.aadt_year})`
      );
    } catch {
      setMessage('ODOT lookup failed. Try again.');
    } finally {
      setDerivingId(null);
    }
  };

  const deriveAllRates = async () => {
    if (!window.confirm('Derive every zone rate from ODOT traffic counts? This overwrites the current rates.')) return;
    setDeriveAllBusy(true);
    setMessage('Deriving all zone rates from ODOT — this can take a minute...');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/geofences/derive-rates`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(`Derivation failed: ${data.detail || res.status}`);
        return;
      }
      await fetchGeofences();
      setMessage(`${data.updated_count} zone(s) derived from ODOT, ${data.skipped_count} skipped. Now recalculate sessions so history matches.`);
    } catch {
      setMessage('ODOT lookup failed. Try again.');
    } finally {
      setDeriveAllBusy(false);
    }
  };

  const recalcSessions = async () => {
    if (!window.confirm('Recalculate impressions for every GPS-estimated session using the current zone rates?')) return;
    setRecalcBusy(true);
    setMessage('Recalculating historical sessions...');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sessions/recalculate-impressions`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(`Recalculation failed: ${data.detail || res.status}`);
        return;
      }
      setMessage(
        `${data.changed} session(s) updated, ${data.unchanged} unchanged. ` +
        `Total impressions ${data.impressions_before.toLocaleString()} → ${data.impressions_after.toLocaleString()}.`
      );
    } catch {
      setMessage('Recalculation failed. Try again.');
    } finally {
      setRecalcBusy(false);
    }
  };

  const seedFromPopulationZone = (zone) => {
    setPendingPolygon(zone.polygon);
    setNewZoneName(zone.name);
    setNewZoneRate(String(densityToSuggestedRate(zone.density)));
    setMessage(
      `Loaded "${zone.name}" (${zone.density.toLocaleString()} people/sq mi → ${densityToLabel(zone.density)}). ` +
      `Adjust the rate if needed, then click Save Geofence.`
    );
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        setAuthenticated(true);
        fetchGeofences();
      } else {
        setMessage('Incorrect admin key');
      }
    } catch (err) {
      console.error('Admin auth failed:', err);
      setMessage('Unable to reach server. Try again.');
    }
  };

  const fetchGeofences = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/geofences`);
      if (res.ok) {
        const data = await res.json();
        setGeofences(data.geofences || []);
      }
    } catch (err) {
      console.error('Failed to load geofences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreated = (e) => {
    const layer = e.layer;
    const latlngs = layer.getLatLngs()[0].map(ll => [ll.lat, ll.lng]);
    setPendingPolygon(latlngs);
    // Remove the drawn layer — we'll render it via React state after saving
    if (featureGroupRef.current) {
      featureGroupRef.current.removeLayer(layer);
    }
    setMessage('Polygon drawn. Enter a name and rate, then click Save.');
  };

  const savePendingZone = async () => {
    if (!pendingPolygon || !newZoneName || !newZoneRate) {
      setMessage('Please draw a polygon and fill in name + rate.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/geofences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newZoneName,
          polygon: pendingPolygon,
          impressions_per_hour: parseInt(newZoneRate),
          color: newZoneColor,
        }),
      });
      if (res.ok) {
        const saved = await res.json();
        setGeofences(prev => [...prev, saved]);
        setPendingPolygon(null);
        setNewZoneName('');
        setNewZoneRate('');
        setMessage(`Geofence "${saved.name}" saved!`);
      }
    } catch {
      setMessage('Error saving geofence.');
    }
  };

  const deleteGeofence = async (id, name) => {
    if (!window.confirm(`Delete geofence "${name}"?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/geofences/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setGeofences(prev => prev.filter(g => g.id !== id));
        setMessage(`Deleted "${name}"`);
      }
    } catch {
      setMessage('Error deleting geofence.');
    }
  };

  if (!authenticated) {
    return (
      <div className="bg-cream-light min-h-screen flex items-center justify-center">
        <form onSubmit={handleLogin} className="bg-white p-8 rounded-2xl shadow-lg max-w-sm w-full">
          <div className="text-center mb-6">
            <Shield className="text-navy mx-auto mb-3" size={40} />
            <h2 className="text-xl font-bold text-navy">Admin Access</h2>
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin password"
            className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl mb-4 focus:border-navy focus:outline-none"
            data-testid="admin-password-input"
          />
          {message && <p className="text-red-600 text-sm mb-3">{message}</p>}
          <button type="submit" className="w-full bg-navy text-cream py-3 rounded-xl font-semibold" data-testid="admin-login-btn">
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-cream-light min-h-screen" data-testid="admin-geofences">
      <section className="bg-navy py-6">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-cream">Geofence Manager</h1>
          <div className="flex items-center gap-3">
            <a href="/admin/manual-data" className="text-cream/80 hover:text-cream text-sm underline">
              Manual data
            </a>
            <a href="/admin/advertisers" className="text-cream/80 hover:text-cream text-sm underline">
              Advertisers
            </a>
            <a href="/admin/sync-health" className="text-cream/80 hover:text-cream text-sm underline">
              Sync health
            </a>
            <span className="text-cream/60 text-sm">{geofences.length} zone(s)</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {message && (
          <div className="bg-navy/10 text-navy p-3 rounded-xl mb-4 text-sm">{message}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2">
            {/* Heatmap toggle + legend */}
            <div className="bg-white rounded-xl px-3 py-2 mb-2 flex items-center justify-between flex-wrap gap-2 border border-navy/10">
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid="admin-heatmap-toggle">
                <input
                  type="checkbox"
                  checked={showHeatmap}
                  onChange={(e) => setShowHeatmap(e.target.checked)}
                  className="w-4 h-4 accent-navy"
                />
                <span className="font-semibold">Population density</span>
                <span className="text-navy/50 text-xs ml-1">Click a zone to seed a new geofence</span>
              </label>
              {showHeatmap && (
                <div className="flex items-center gap-1.5 text-xs text-navy/70">
                  <span>Lower</span>
                  <span className="w-4 h-3" style={{ background: 'rgba(30,58,95,0.10)' }} />
                  <span className="w-4 h-3" style={{ background: 'rgba(30,58,95,0.22)' }} />
                  <span className="w-4 h-3" style={{ background: 'rgba(30,58,95,0.36)' }} />
                  <span className="w-4 h-3" style={{ background: 'rgba(30,58,95,0.50)' }} />
                  <span className="w-4 h-3" style={{ background: 'rgba(30,58,95,0.65)' }} />
                  <span>Higher</span>
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ height: '550px' }}>
              <MapContainer center={[39.98, -82.99]} zoom={10} style={{ height: '100%', width: '100%' }}>
                <OpenStreetMapTiles mapId="admin-geofences-map" />

                {/* Population density heatmap (darker = more populated) — click to seed a new geofence */}
                {showHeatmap && POPULATION_ZONES.map((z) => (
                  <Polygon
                    key={`pop-${z.name}`}
                    positions={z.polygon}
                    pathOptions={{
                      color: '#1e3a5f',
                      fillColor: '#1e3a5f',
                      fillOpacity: densityToOpacity(z.density),
                      weight: 0.5,
                      opacity: 0.5,
                    }}
                    eventHandlers={{
                      click: () => seedFromPopulationZone(z),
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: 180 }}>
                        <p style={{ margin: 0, fontWeight: 700 }}>{z.name}</p>
                        <p style={{ margin: '4px 0 6px', fontSize: 12 }}>
                          {z.density.toLocaleString()} people/sq mi · <em>{densityToLabel(z.density)}</em>
                        </p>
                        <p style={{ margin: 0, fontSize: 12 }}>
                          Suggested rate: <strong>{densityToSuggestedRate(z.density).toLocaleString()} impr/hr</strong>
                        </p>
                        <button
                          onClick={() => seedFromPopulationZone(z)}
                          style={{
                            marginTop: 8, padding: '6px 10px', background: '#1e3a5f',
                            color: '#FFF8E7', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            border: 'none', cursor: 'pointer', width: '100%',
                          }}
                        >
                          Create geofence from this zone
                        </button>
                      </div>
                    </Popup>
                  </Polygon>
                ))}

                {/* I-270 outer beltway boundary (reference) */}
                <Polygon
                  positions={I270_POLYGON}
                  pathOptions={{
                    color: '#f97316', fillColor: '#f97316',
                    fillOpacity: 0.04, weight: 2, dashArray: '6 4',
                  }}
                />

                <FeatureGroup ref={featureGroupRef}>
                  <EditControl
                    position="topright"
                    onCreated={handleCreated}
                    draw={{
                      rectangle: false,
                      circle: false,
                      circlemarker: false,
                      marker: false,
                      polyline: false,
                      polygon: {
                        allowIntersection: false,
                        shapeOptions: { color: newZoneColor, fillOpacity: 0.2 },
                      },
                    }}
                    edit={{ edit: false, remove: false }}
                  />
                </FeatureGroup>
                {/* Render saved geofences */}
                {geofences.filter(isValidPolygon).map((gf) => (
                  <Polygon
                    key={gf.id}
                    positions={safePolygonPositions(gf.polygon)}
                    pathOptions={{ color: gf.color || '#3388ff', fillOpacity: 0.15, weight: 2 }}
                  >
                    <Popup>
                      <strong>{gf.name}</strong><br />
                      {gf.impressions_per_hour.toLocaleString()} impressions/hr
                    </Popup>
                  </Polygon>
                ))}
                {/* Render pending polygon */}
                {pendingPolygon && (
                  <Polygon
                    positions={pendingPolygon.map(p => [p[0], p[1]])}
                    pathOptions={{ color: newZoneColor, fillOpacity: 0.3, weight: 2, dashArray: '5,5' }}
                  />
                )}
              </MapContainer>
            </div>
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            {/* New zone form */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Plus size={18} /> New Geofence</h3>
              <p className="text-navy/60 text-xs mb-3">Draw a polygon on the map, then fill in the details below.</p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  placeholder="Zone name (e.g., Franklin County)"
                  className="w-full px-3 py-2 border border-navy/20 rounded-lg text-sm focus:border-navy focus:outline-none"
                  data-testid="geofence-name-input"
                />
                <input
                  type="number"
                  value={newZoneRate}
                  onChange={(e) => setNewZoneRate(e.target.value)}
                  placeholder="Impressions per hour"
                  className="w-full px-3 py-2 border border-navy/20 rounded-lg text-sm focus:border-navy focus:outline-none"
                  data-testid="geofence-rate-input"
                />
                <div className="flex items-center gap-2">
                  <label className="text-navy/60 text-sm">Color:</label>
                  <input
                    type="color"
                    value={newZoneColor}
                    onChange={(e) => setNewZoneColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                </div>
                <button
                  onClick={savePendingZone}
                  disabled={!pendingPolygon || !newZoneName || !newZoneRate}
                  className="w-full bg-navy text-cream py-2 rounded-lg font-medium text-sm disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="save-geofence-btn"
                >
                  <Save size={16} /> Save Geofence
                </button>
              </div>
            </div>

            {/* ODOT rate derivation */}
            <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="odot-panel">
              <h3 className="font-bold text-navy mb-1 flex items-center gap-2">
                <Database size={18} /> ODOT-Derived Rates
              </h3>
              <p className="text-navy/60 text-xs mb-3">
                Replaces typed-in rates with values computed from Ohio DOT's public AADT
                traffic counts inside each polygon. Every zone stores its segments, count
                year, and the factors used.
              </p>
              <div className="space-y-2">
                <button
                  onClick={deriveAllRates}
                  disabled={deriveAllBusy}
                  className="w-full bg-navy text-cream py-2 rounded-lg font-medium text-sm disabled:opacity-40"
                  data-testid="derive-all-rates-btn"
                >
                  {deriveAllBusy ? 'Deriving…' : 'Derive all zone rates from ODOT'}
                </button>
                <button
                  onClick={recalcSessions}
                  disabled={recalcBusy}
                  className="w-full border border-navy text-navy py-2 rounded-lg font-medium text-sm disabled:opacity-40"
                  data-testid="recalc-sessions-btn"
                >
                  {recalcBusy ? 'Recalculating…' : 'Recalculate all session impressions'}
                </button>
              </div>
            </div>

            {/* Existing geofences list */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-navy mb-3">Active Geofences</h3>
              {loading ? (
                <p className="text-navy/50 text-sm">Loading...</p>
              ) : geofences.length === 0 ? (
                <p className="text-navy/50 text-sm">No geofences yet. Draw one on the map.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {geofences.map((gf) => (
                    <div key={gf.id} className="p-3 bg-cream rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: gf.color || '#3388ff' }} />
                          <div>
                            <p className="text-navy font-medium text-sm">{gf.name}</p>
                            <p className="text-navy/50 text-xs">{gf.impressions_per_hour.toLocaleString()} impr/hr</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => deriveRate(gf)}
                            disabled={derivingId === gf.id}
                            title="Derive this rate from ODOT traffic counts"
                            className="text-navy hover:text-navy-dark p-1 disabled:opacity-40"
                            data-testid={`derive-rate-${gf.id}`}
                          >
                            <RefreshCw size={15} className={derivingId === gf.id ? 'animate-spin' : ''} />
                          </button>
                          <button
                            onClick={() => deleteGeofence(gf.id, gf.name)}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`delete-geofence-${gf.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      {gf.rate_source && (
                        <p className="text-navy/45 text-[11px] mt-2 leading-snug" data-testid={`rate-source-${gf.id}`}>
                          ODOT {gf.rate_source.aadt_year}: weighted AADT{' '}
                          {gf.rate_source.aadt_weighted?.toLocaleString()} across{' '}
                          {gf.rate_source.segment_count} segments · occupancy{' '}
                          {gf.rate_source.factors?.persons_per_vehicle} · visibility{' '}
                          {gf.rate_source.factors?.visibility_factor}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGeofences;
